import { ModalService } from './modal.service';
import { NavController } from '@ionic/angular';
import { ApiService } from './api.service';
import { BehaviorSubject } from 'rxjs';
import { AuthStatus, Enterprise, User } from '../models/models';
import { Injectable } from '@angular/core';
import { DataService } from './data.service';
import { FormGroup } from '@angular/forms';
import { AppComponent } from '../app.component';
import { ToastService } from './toast.service';
import { LoaderUtils } from '../utils/loader.utils';
import { UserService } from './user.service';

type LoginApiResponse =
  | { token: string; user: User }
  | {
      twoFactor: {
        challengeUuid: string;
        channel: 'email' | 'sms';
        destinationMasked?: string;
        pendingToken: string;
      };
    };

export type LoginOutcome =
  | { kind: 'session'; user: User }
  | {
      kind: 'twoFactor';
      twoFactor: {
        challengeUuid: string;
        channel: 'email' | 'sms';
        destinationMasked?: string;
        pendingToken: string;
      };
    };

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  authStatus = new BehaviorSubject<AuthStatus>(null);
  user: BehaviorSubject<User> = new BehaviorSubject<User>(null);
  redirectUrl: string;
  requestPasswordResetForm: FormGroup;
  passwordCodeForm: FormGroup;
  passwordChangeForm: FormGroup;

  constructor(
    private apiService: ApiService,
    private navCtrl: NavController,
    private modalService: ModalService,
    private dataService: DataService,
    private toastService: ToastService,
    private userService: UserService
  ) {}

  async init() {
    const token = await this.dataService.getItem<string>('token');
    if (token) {
      const savedUser = await this.dataService.getItem<User>('user');
      const enterprise = await this.dataService.getItem<Enterprise>('enterprise');
      // Get user from API
      this.userService.getUser(savedUser.id, token).then(async (user) => {
        await this.setUser(user);
        AppComponent.enterprise.next(enterprise);
        if (user.accessType === 'mutuelle' && !enterprise) {
          this.navCtrl.navigateForward('/select-enterprise');
        }
        this.authStatus.next(new AuthStatus(true, !!token, user.id, token, AppComponent.enterprise.value, user));
      }).catch(async (res) => {
        this.logout(false);
      });
    } else {
      this.authStatus.next(new AuthStatus(true, false));
    }
    ApiService.token = token;
  }

  checkToken(): Promise<string> {
    return null;
  }

  async setToken(token: string): Promise<string> {
    ApiService.token = token;
    if (token) {
      this.dataService.setItem('token', token, true);
    } else {
      this.dataService.removeItem('token');
    }
    return token;
  }

  async setEnterprise(enterprise: Enterprise): Promise<Enterprise> {
    AppComponent.enterprise.next(enterprise);
    if (enterprise) {
      this.dataService.setItem('enterprise', enterprise, true);
    } else {
      this.dataService.removeItem('enterprise');
    }
    return enterprise;
  }

  async setUser(user: User): Promise<User> {
    this.user.next(user ? new User(user) : null);
    if (user) {
      this.dataService.setItem('user', user, true);
    } else {
      this.dataService.removeItem('user');
    }
    return user;
  }

  async login(
    data: { identifier: string; password: string },
  ): Promise<LoginOutcome> {
    return new Promise<LoginOutcome>(async (resolve, reject) => {
      this.apiService
        .post<LoginApiResponse>('/login', data)
        .subscribe({
          next: async (res) => {
            if (!('token' in res)) {
              resolve({ kind: 'twoFactor', twoFactor: res.twoFactor });
              return;
            }
            const { token, user } = res;
            await this.setToken(token);
            await this.setUser(user);
            this.setEnterprise(null);
            user.enterpriseId = null;
            user.enterpriseName = null;
            this.authStatus.next(
              new AuthStatus(true, true, user.id, token, null, user),
            );
            resolve({ kind: 'session', user });
          },
          error: (res) => {
            console.log(res?.error);
            reject(res?.error);
          },
        });
    });
  }

  async completeLoginTwoFactor(payload: {
    pendingToken: string;
    challengeUuid: string;
    code: string;
  }): Promise<{ user: User }> {
    return new Promise<{ user: User }>(async (resolve, reject) => {
      this.apiService
        .post<{ token: string; user: User }>(
          '/login/complete-two-factor',
          payload,
        )
        .subscribe({
          next: async (res) => {
            await this.setToken(res.token);
            await this.setUser(res.user);
            this.setEnterprise(null);
            res.user.enterpriseId = null;
            res.user.enterpriseName = null;
            this.authStatus.next(
              new AuthStatus(
                true,
                true,
                res.user.id,
                res.token,
                null,
                res.user,
              ),
            );
            resolve({ user: res.user });
          },
          error: (res) => {
            console.log(res?.error);
            reject(res?.error);
          },
        });
    });
  }

  async logout(forceModal = false) {
    this.setToken(null);
    this.setUser(null);
    this.setEnterprise(null);
    this.authStatus.next(new AuthStatus(true, false));
    this.dataService.reset();
    await this.modalService.closeAll();
    LoaderUtils.dismiss();
    this.navCtrl.navigateForward('/login');
    if (forceModal) {
      await this.toastService.open({
        message: 'Vous avez été déconnecté, veuillez vous reconnecter pour accéder à vos services.'
      });
    }
  }

  async changePassword(data): Promise<any> {
    return new Promise(async (resolve, reject) => {
      this.apiService.post<any>('/change-password', data, true).subscribe({
        next: (res) => {
          resolve(res);
        },
        error: (res) => {
          console.log(res?.error);
          reject(res?.error);
        },
      });
    });
  }

  async requestPasswordReset(data: any): Promise<any> {
    return new Promise(async (resolve, reject) => {
      this.apiService.post<any>('/lost-password', data).subscribe({
        next: (res) => {
          resolve(res);
        },
        error: (res) => {
          console.log(res?.error);
          reject(res?.error);
        },
      });
    });
  }

  async checkPasswordCode(data): Promise<any> {
    return new Promise(async (resolve, reject) => {
      this.apiService.post<any>('/lost-password/check-code', data).subscribe({
        next: (res) => {
          resolve(res);
        },
        error: (res) => {
          console.log(res?.error);
          reject(res?.error);
        },
      });
    });
  }
}
